import {
  ApolloClient,
  from,
  HttpLink,
  InMemoryCache,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { createClient } from 'graphql-ws';
import { jwtDecode, JwtPayload } from 'jwt-decode';
import { fetchToken } from './fetchToken';
import { authAtom, jotaiStore } from '~/atoms/authAtom';

export const getWsUrl = (httpUrl: string) => {
  if (httpUrl.startsWith('https://')) {
    return httpUrl.replace(/^https:\/\//, 'wss://');
  }

  if (httpUrl.startsWith('http://')) {
    return httpUrl.replace(/^http:\/\//, 'ws://');
  }

  throw new Error('Invalid API URL');
};

let accessToken: string | null = null;
let decodedToken: JwtPayload | null = null;

// Call this to force a token refresh on next request (e.g., after org change)
export const invalidateGraphQLToken = () => {
  console.log('[GraphQL] Invalidating cached token');
  accessToken = null;
  decodedToken = null;
};

export const isTokenExpired = (token: JwtPayload | null) => {
  if (token) {
    return token.exp && token.exp < Date.now() / 1000;
  }
  return true;
};

export const getToken = async () => {
  if (isTokenExpired(decodedToken)) {
    const { token } = await fetchToken();
    try {
      const decoded = jwtDecode(token);
      decodedToken = decoded;
    } catch (e) {
      console.error('Failed to decode JWT:', e);
    }

    accessToken = token;
  }

  return accessToken;
};

export const getGraphQLClient = () => {
  const authLink = setContext(async (_, { headers }) => {
    const token = await getToken();
    const auth = jotaiStore.get(authAtom);
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
        'X-Hasura-Role': auth?.auth?.orgRole || 'anonymous',
      },
    };
  });

  const httpLink = new HttpLink({
    uri: process.env.REACT_HASURA_URL,
  });

  const wsLink = new GraphQLWsLink(
    createClient({
      url: getWsUrl(process.env.REACT_HASURA_URL),
      connectionParams: async () => {
        const token = await getToken();
        const auth = jotaiStore.get(authAtom);
        return {
          headers: {
            Authorization: token ? `Bearer ${token}` : '',
            'X-Hasura-Role': auth?.auth?.orgRole || 'anonymous',
          },
          reconnect: true,
        };
      },
    }),
  );

  const splitLink = split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    wsLink,
    from([authLink, httpLink]),
  );

  const graphQLClient = new ApolloClient({
    link: splitLink,
    cache: new InMemoryCache(),
  });

  return graphQLClient;
};
